import { LocaleData } from '../../../models/locale/locale-data';
import { LandingVariantCopy } from '../../../models/landing/variant';

// New-UX (`-v1`) shared copy — the approved redesign strings (mockup pack
// round 3, 26-Aug-2026 product feedback). The three `-v1` variant keys reuse
// this block; ` **` footnote markers on the CTA headings are appended by the
// components, not stored here.
const v2Copy: LandingVariantCopy = {
  banner: 'JETZT REGISTRIEREN',
  heroCopy: 'Herzlichen Glückwunsch zu Ihrem Kauf!',
  heroSubCopy1:
    'Registrieren Sie jetzt Ihr Gerät innerhalb von 60 Tagen, um Ihre kostenlosen Vorteile freizuschalten',
  benefitTicks: [
    '10 Jahre kostenlose Garantie auf Ersatzteile*',
    'Originalteile von Beko',
    'Optionaler zusätzlicher Schutz bei Unfallschäden und Pannen',
  ],
  heroDisclaimer:
    'Die Garantie deckt Motoren, Pumpen, Steuerplatinen und andere berechtigte Komponenten ab.',
  registration: 'Nur einen Anruf entfernt',
  ctaPhoneText: 'Jetzt anrufen: 0800 000 0062',
  phoneSubCopy: 'Die Hotline ist Montag bis Freitag von 8:00 bis 18:30 Uhr erreichbar.',
  onlineCopy: 'In wenigen Minuten online registrieren',
  ctaOnlineText: 'Online registrieren',
  sixtyDayHeader: 'Innerhalb von 60 Tagen registrieren',
  sixtyDayBody:
    'Aktivieren Sie Ihre kostenlose 10-Jahres-Garantie auf Ersatzteile, bevor Ihr Registrierungszeitraum endet',
};

// LP1 hybrid (`phone-only-v2`, Sept 2026) — LP1 COPY V2_SEPT 2026 2.xlsx (BEKO tab)
// as applied in the approved mockups (claude-notes/lp-hybrid-lp1). Every line
// matches Beko control or V1. Dials the same number as V1: `ctaPhoneLink` is
// V1's (undefined = the base link). The `**` on the first heading is appended
// by PhoneOnlyV2.
const lp1HybridCopy: LandingVariantCopy = {
  banner: 'Jetzt registrieren',
  offerHeadline: ['10 Jahre Garantie', 'auf Ersatzteile*'],
  sixtyDayHeader: 'Registrierung erforderlich innerhalb von 60 Tagen nach Gerätekauf',
  registration: 'Jetzt kostenfrei anrufen & aktivieren',
  ctaPhoneText: 'Jetzt anrufen: 0800 000 0062',
  ctaPhoneLink: v2Copy.ctaPhoneLink,
  benefitsHeader: 'Schalten Sie jetzt Ihre kostenlosen Vorteile frei:',
  benefitTicks: [
    '10 Jahre kostenlose Garantie auf Ersatzteile',
    'Originalteile von Beko',
    'Optionaler zusätzlicher Schutz bei Unfallschäden und Pannen',
  ],
  phoneSubCopy: 'Die Hotline ist Montag bis Freitag von 8:00 bis 18:30 Uhr erreichbar.',
  repeatHeading: 'Bereit, Ihr Gerät zu registrieren?',
  ctaPhoneRepeatText: 'Jetzt anrufen: 0800 000 0062',
};

export const data: LocaleData = {
  banner: 'Aktivieren Sie Ihre GRATIS',
  heroNumber: '10',
  heroCopy: 'Jahre Garantie auf Ersatzteile*',
  heroSubCopy1: '<b>Aktivierung erforderlich</b>',
  heroSubCopy2: 'innerhalb von 60 Tagen nach Gerätekauf',
  registration: 'Jetzt kostenfrei anrufen & aktivieren**',
  ctaPhoneText: '0800 000 0062',
  ctaPhoneLink: 'tel:08000000062',
  phoneSubCopy:
    'Die Aktivierung per Anruf ist schnell und bietet Ihnen zusätzliche Informationen zu Ihrer Hersteller- und 10-Jahres-Garantie auf Ersatzteile. Die Öffnungszeiten der Service-Hotline sind Montag bis Freitag 8:00 - 18:30 Uhr.',
  // Matches production's mid-sentence link ("<0>hier</0>"); production's
  // "Allternativ" typo corrected to "Alternativ". Rendered via parseText with
  // ctaOnlineLink as link index 0.
  onlineCopy: 'Alternativ können Sie sich auch <0>hier</0> online registrieren.',
  ctaOnlineText: 'hier',
  ctaOnlineLink: 'https://beko.register-appliance.com/RegistroPorMarca?country=DE&language=de',
  onlineJourneyLaunch: 'Live',
  variants: {
    'phone-only-v1': v2Copy,
    'phone-and-link-v1': v2Copy,
    'phone-only-v2': lp1HybridCopy,
    'link-only-v1': {
      ...v2Copy,
      ctaOnlineText: 'Online registrieren',
      linesClosedMessage:
        'Unsere Telefonleitungen sind derzeit geschlossen, aber Sie können sich jederzeit online registrieren',
      phoneNumberAsterix: [
        '** Domestic & General Insurance Europe AG registriert Ihr Gerät, in Kooperation mit der Beko Germany GmbH. Bei Interesse wird Ihnen Domestic & General Angebote zum Abschluss eines Versicherungsschutzes für Ihre Beko Hausgeräte unterbreiten. Informationen über die Datenschutzhinweise der Beko Germany GmbH und Domestic & General Insurance Europe AG erhalten Sie auf <0>www.domesticandgeneral.com/beko</0>',
      ],
      phoneNumberAsterixLinks: ['https://www.domesticandgeneral.com/beko'],
    },
    // LP3 COPY V1_JULY 2026.xlsx (BEKO tab; ** added per master sheet).
    'link-only': {
      registration:
        'Registrieren Sie jetzt Ihr Gerät, um Ihre kostenlosen Vorteile zu aktivieren**',
      onlineCopy:
        'Unsere Telefonleitungen sind derzeit geschlossen, aber Sie können sich jederzeit online registrieren',
      ctaOnlineText: 'Online registrieren',
      phoneNumberAsterix: [
        '** Domestic & General Insurance Europe AG registriert Ihr Gerät, in Kooperation mit der Beko Germany GmbH. Bei Interesse wird Ihnen Domestic & General Angebote zum Abschluss eines Versicherungsschutzes für Ihre Beko Hausgeräte unterbreiten. Informationen über die Datenschutzhinweise der Beko Germany GmbH und Domestic & General Insurance Europe AG erhalten Sie auf <0>www.domesticandgeneral.com/beko</0>',
      ],
      phoneNumberAsterixLinks: ['https://www.domesticandgeneral.com/beko'],
    },
  },
  benefitsHeader: 'Ihre Vorteile der Aktivierung:',
  benefits: [
    '10 Jahre lang kostenlose Ersatzteilgarantie',
    'Aktivierung gilt als Kaufnachweis',
    'Wichtige Geräte- und Sicherheitsupdates',
  ],
  footerLinks: [
    { text: 'Impressum', link: '/terms' },
    {
      text: 'Datenschutzhinweise',
      link: 'https://www.domesticandgeneral.com/beko/datenschutzhinweise',
    },
    { text: 'Cookie-Richtlinien', link: '/cookies' },
  ],
  footerCopy:
    'Diese Website steht im Eigentum der Domestic & General Group Limited und wird von der Domestic & General Insurance Europe AG, Hagenauer Straße 44, 65203 Wiesbaden, Deutschland, betrieben. Die Domestic & General Insurance Europe AG ist von der Bundesanstalt für Finanzdienstleistungsaufsicht (BaFin) zugelassen und reguliert.',
  heroAsterix: [
    '* Für diese Aktion gelten die besonderen Ersatzteilaktions-Bedingungen der Beko Germany GmbH, Rahmannstraße 3, 65760 Eschborn. Diese können unter <0>https://www.beko.com/de-de/service/garantie</0> eingesehen werden. Ihre Rechte aus Ihrer 2-jährigen Herstellergarantie (diese schließen kostenlose Ersatzteile ein) bleiben von dieser Aktion unberührt und Sie können diese auch ohne Aktivierung geltend machen. Ihre gesetzlichen Rechte gegenüber dem Verkäufer des Geräts bleiben von dieser Garantie unberührt. Nach der Aktivierung erhalten Sie für insgesamt 10 Jahre eine kostenlose Ersatzteilgarantie (nach Ablauf der 2-jährigen Herstellergarantie).',
  ],
  heroAsterixLinks: ['https://www.beko.com/de-de/service/garantie'],
  phoneNumberAsterix: [
    '** Hotline von Domestic & General Insurance Europe AG in Kooperation mit der Beko Germany GmbH. Bei Interesse wird Ihnen Domestic & General Angebote zum Abschluss eines Versicherungsschutzes für Ihre Beko Hausgeräte unterbreiten. Informationen über die Datenschutzhinweise der Beko Germany GmbH und Domestic & General Insurance Europe AG erhalten Sie auf <0>www.domesticandgeneral.com/beko</0>',
  ],
  phoneNumberAsterixLinks: ['https://www.domesticandgeneral.com/beko'],
  additionalSmallPrint:
    'Diese Aktion gilt nur für Kunden, die in der Bundesrepublik Deutschland ansässig sind. Offer only available for customers based in the Federal Republic of Germany. All other markets register at <0>register.beko.com</0> for other offers.',
  additionalSmallPrintLinks: ['https://register.beko.com'],
  languageSwitcherLabel: 'Land',
  languageSwitcherPlaceholder: 'Wählen Sie eine Sprache',
  languageList: [
    { label: 'Deutschland', value: 'de-DE' },
    { label: 'Frankreich', value: 'fr-FR' },
    { label: 'Italien', value: 'it-IT' },
    { label: 'Spanien', value: 'es-ES' },
    { label: 'Niederlande', value: 'nl-NL' },
    { label: 'Belgien (Französisch)', value: 'be-FR' },
    { label: 'Belgien (Niederländisch)', value: 'be-NL' },
  ],
  cookiePolicy: {
    title: 'Cookie-Richtlinie',
    sections: [
      {
        heading: 'Was sind Cookies?',
        description: [
          'Cookies sind kleine Textdateien, die beim Besuch einer Website an Ihr Gerät gesendet werden. Sie verbleiben auf Ihrem Gerät und werden bei jedem nächsten Besuch an die Website zurückgesendet, von der sie stammen. Cookies erfassen Daten über Sie, wie beispielsweise Ihre Spracheinstellungen oder Anmeldeinformationen. Diese Cookies werden von uns gesetzt und als Erstanbieter-Cookies bezeichnet. Wir verwenden auch Drittanbieter-Cookies. Diese stammen von einer anderen Domain als der der von Ihnen besuchten Website. Diese können uns helfen, unsere Werbung und unser Marketing zu verbessern. Wir verwenden Cookies und andere Tracking-Technologien für folgende Zwecke:',
        ],
      },
      {
        heading: 'Cookies verwalten',
        description: [
          'Um Ihre Cookie-Einstellungen zu ändern, nutzen Sie unser Cookie-Verwaltungstool oder das Sicherheitssymbol auf jeder Seite. Sie können die Verwendung einiger oder aller Cookies und ähnlicher Technologien, die wir auf der Website verwenden, blockieren, indem Sie auf die verschiedenen Cookie-Kategorien klicken. Bitte beachten Sie jedoch, dass dies Ihre Erfahrung mit der Website und unseren Diensten beeinträchtigen kann. Beachten Sie außerdem, dass beim Löschen aller Cookies in Ihrem Browser auch alle Cookies gelöscht werden, die Ihre Einstellungen speichern.',
          'Die meisten Browser erklären Ihnen, wie Sie die Annahme neuer Cookies unterbinden, sich beim Empfang eines neuen Cookies benachrichtigen lassen und Cookies vollständig deaktivieren können. Um bereits in Ihrem Browser gespeicherte Cookies zu löschen, können Sie Ihren Browserverlauf löschen und Cookies löschen oder löschen lassen.',
          'Einige Browser, wie Chrome und Firefox, ermöglichen Ihnen den Inkognito-Modus. Dadurch wird die Datenmenge auf Ihrem Computer begrenzt und alle dauerhaften Cookies werden nach Beendigung Ihrer Browsersitzung automatisch gelöscht.',
          'Alternativ können Sie <0>www.aboutcookies.org</0> besuchen. Dort finden Sie umfassende Informationen zum Entfernen von Cookies in verschiedenen Browsern.',
          'Wenn Sie mehr über Cookies im Allgemeinen erfahren möchten und wissen möchten, wie Sie überprüfen können, welche Drittanbieter Cookies auf Ihrem Mobilgerät aktiviert haben und wie Sie einige dieser Cookies deaktivieren können, besuchen Sie <1>www.networkadvertising.org/mobile-choices</1>. Auf Ihrem Mobilgerät können Sie außerdem „Ad-Tracking einschränken“ (auf iOS-Geräten) oder „Interessenbezogene Werbung deaktivieren“ (auf Android-Geräten) auswählen. Dadurch können Sie die Verwendung von Informationen über Ihre App-Nutzung für die Bereitstellung interessenbezogener Werbung einschränken.',
        ],
        links: [
          'https://www.aboutcookies.org',
          'https://www.networkadvertising.org/mobile-choices',
        ],
      },
      {
        heading: 'Websites Dritter',
        description: [
          'Diese Website kann Links zu anderen Websites enthalten oder mit diesen verknüpft sein. Wenn Sie auf die verlinkte Website klicken, verlassen Sie die von uns kontrollierte Website und deren Inhalte und werden mit der Website des Drittanbieters verknüpft. Wir haben keinen Einfluss auf die Inhalte der verlinkten Website und übernehmen keine Verantwortung für deren Informationen und deren Betrieb. Sobald wir Kenntnis davon erhalten, dass dort rechtswidrige oder sonstige Inhalte bereitgestellt werden, werden wir den Link zur verlinkten Website löschen. Die Betreiber der verlinkten Website können Informationen von Ihnen erheben, die sie gemäß ihrer Datenschutzrichtlinie verwenden, die von unserer abweichen kann. Wir haben keinen Einfluss auf die Cookie-Einstellungen der verlinkten Website. Wir empfehlen Ihnen daher, die verlinkten Websites zu besuchen, um weitere Informationen zu deren Cookies und deren Verwaltung zu erhalten.',
        ],
      },
      {
        heading: 'Sicherheit',
        description: [
          'Wir legen großen Wert auf die Sicherheit unserer Website und eine betrugssichere Kundenschnittstelle. Alle ausgetauschten Informationen werden mit SSL-Software verschlüsselt und wir haben alle Anstrengungen unternommen, um sicherzustellen, dass Ihre persönlichen Daten vor unbefugtem Zugriff geschützt sind.',
        ],
      },
    ],
  },
  termsAndConditions: {
    title: 'Impressum',
    content: [
      {
        heading: 'Impressum & rechtliche Hinweise',
      },
      {
        heading: 'Impressum',
        paragraphs: [
          'Domestic & General Insurance Europe AG',
          'Hagenauer Str. 44',
          '65203 Wiesbaden',
          'eingetragen unter HRB Nummer 30859',
          'Amtsgericht Wiesbaden',
        ],
      },
      {
        heading: 'Vorsitzender des Aufsichtsrats:',
        paragraphs: ['David Tyler'],
      },
      {
        heading: 'Vorstand:',
        paragraphs: [
          'Matthew Crummack (Vorsitzender), Thomas Goode, Matthias Kammermann, Sven Wick',
          'Domestic & General Insurance Europe AG (Domestic & General) ist von der Bundesanstalt für Finanzdienstleistungsaufsicht zugelassen, wird von dieser reguliert, und dort unter der Registernummer 5227 geführt.',
          'Tel: <0>+49 (0)611 308 78 0</0>',
          'Fax: <1>+49 (0)611 308 78 55</1>',
          'Email: <2>infodtc@domesticandgeneral.com</2>',
          'USt-IdNr.: DE322598066',
          'Steuernummer: 014 223 10798',
        ],
        links: ['tel:+49611308780', 'tel:+496113087855', 'mailto:infodtc@domesticandgeneral.com'],
      },
      {
        heading: 'Zuständige Aufsichtsbehörde:',
        paragraphs: [
          'Bundesanstalt für Finanzdienstleistungsaufsicht (BaFin)',
          'Graurheindorfer Straße 108',
          '53117 Bonn',
          'Website: <0>www.bafin.de</0>',
        ],
        links: ['https://www.bafin.de'],
      },
      {
        heading: 'Online-Streitbeilegungsplattform (OS-Plattform)',
        paragraphs: [
          'Die EU stellt eine Plattform zur Klärung von Online-Streitigkeiten bereit. Diese Plattform dient als eine zentrale Anlaufstelle für Verbraucher und Unternehmer, die Streitigkeiten außergerichtlich beilegen möchten. Unter folgendem Link können Sie diese aufrufen: wwww.ec.europa.eu/consumers/odr',
        ],
      },
      {
        heading: 'Rechtliche Hinweise',
      },
      {
        heading: '1. Nutzung der Website',
        paragraphs: [
          'Mit der reinen Nutzung der Website des Anbieters kommt keinerlei Vertragsverhältnis zwischen dem Nutzer und dem Anbieter zustande. Namentlich gekennzeichnete Beiträge dieser Website geben die Meinung des jeweiligen Autors und nicht immer die Meinung des Anbieters wieder.',
        ],
      },
      {
        heading: '2. Externe Links',
        paragraphs: [
          'Diese Website enthält Verknüpfungen zu Websites Dritter ("externe Links"). Diese Websites unterliegen der Haftung der jeweiligen Betreiber. Domestic & General hat bei der erstmaligen Verknüpfung der externen Links die fremden Inhalte daraufhin überprüft, ob etwaige Rechtsverstöße bestehen. Zu dem Zeitpunkt waren keine Rechtsverstöße ersichtlich. Domestic & General hat keinerlei Einfluss auf die aktuelle und zukünftige Gestaltung und auf die Inhalte der verknüpften Seiten. Das Setzen von externen Links bedeutet nicht, dass sich Domestic & General die hinter dem Verweis oder Link liegenden Inhalte zu Eigen macht. Eine ständige Kontrolle der externen Links ist für den Anbieter ohne konkrete Hinweise auf Rechtsverstöße nicht zumutbar. Bei Kenntnis von Rechtsverstößen werden jedoch derartige externe Links unverzüglich gelöscht. Domestic & General bittet um entsprechende Hinweise.',
        ],
      },
      {
        heading: '3. Urheber- und Leistungsschutzrechte',
        paragraphs: [
          'Die auf dieser Website veröffentlichten Inhalte unterliegen dem deutschen Urheber- und Leistungsschutzrecht. Jede vom deutschen Urheber- und Leistungsschutzrecht nicht zugelassene Verwertung bedarf der vorherigen schriftlichen Zustimmung von Domestic & General oder jeweiligen Rechteinhabers. Dies gilt insbesondere für Vervielfältigung, Bearbeitung, Übersetzung, Einspeicherung, Verarbeitung bzw. Wiedergabe von Inhalten in Datenbanken oder anderen elektronischen Medien und Systemen. Inhalte und Rechte Dritter sind dabei als solche gekennzeichnet. Die unerlaubte Vervielfältigung oder Weitergabe einzelner Inhalte oder kompletter Seiten ist nicht gestattet und strafbar. Lediglich die Herstellung von Kopien und Downloads für den persönlichen, privaten und nicht kommerziellen Gebrauch ist erlaubt.',
          'Die Darstellung dieser Website in fremden Frames ist nur mit schriftlicher Erlaubnis zulässig.',
        ],
      },
      {
        heading: '4. Datenschutz',
        paragraphs: [
          'Durch den Besuch der Domestic & General Website können Informationen über den Zugriff (Datum, Uhrzeit, betrachtete Seite) gespeichert werden. Diese Daten gehören nicht zu den personenbezogenen Daten, sondern sind anonymisiert. Sie werden ausschließlich zu statistischen Zwecken ausgewertet. Eine Weitergabe an Dritte, zu kommerziellen oder nichtkommerziellen Zwecken, findet nicht statt. Domestic & General weist ausdrücklich darauf hin, dass die Datenübertragung im Internet (z.B. bei der Kommunikation per E-Mail) Sicherheitslücken aufweisen und nicht lückenlos vor dem Zugriff durch Dritte geschützt werden kann.',
          'Die Verwendung der Kontaktdaten des Impressums zur gewerblichen Werbung ist ausdrücklich nicht erwünscht, es sei denn Domestic & General hatte zuvor seine schriftliche Einwilligung erteilt oder es besteht bereits eine Geschäftsbeziehung. Domestic & General und alle auf dieser Website genannten Personen widersprechen hiermit jeder kommerziellen Verwendung und Weitergabe ihrer Daten.',
        ],
      },
    ],
  },
};
